import { Edit, MoreVertical, Search, Trash2, Truck } from 'lucide-react'

import { useState } from 'react'

import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Input } from '@/components/ui/input'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'

import { useBusiness } from '@/contexts/BusinessContext'

import { ShippingMethod } from '@/types/shipping'

export const ShippingMethods = () => {
  const [searchTerm, setSearchTerm] = useState('')
  const { formatCurrency } = useBusiness()

  // Mock data
  const methods: ShippingMethod[] = [
    {
      id: '1',
      name: 'Standard Shipping',
      zoneId: '1',
      zoneName: 'United States',
      type: 'flat',
      cost: 9.99,
      estimatedDays: '3-5 days',
      enabled: true,
      createdAt: new Date(),
    },
    {
      id: '2',
      name: 'Express Shipping',
      zoneId: '1',
      zoneName: 'United States',
      type: 'flat',
      cost: 24.99,
      estimatedDays: '1-2 days',
      enabled: true,
      createdAt: new Date(),
    },
    {
      id: '3',
      name: 'Free shipping',
      zoneId: '1',
      zoneName: 'United States',
      type: 'free',
      cost: 0,
      minOrderAmount: 100,
      estimatedDays: '5-7 days',
      enabled: true,
      createdAt: new Date(),
    },
    {
      id: '4',
      name: 'International Standard',
      zoneId: '2',
      zoneName: 'Europe',
      type: 'weight',
      cost: 15.0,
      estimatedDays: '7-14 days',
      enabled: true,
      createdAt: new Date(),
    },
  ]

  const filteredMethods = methods.filter((method) =>
    method.name.toLowerCase().includes(searchTerm.toLowerCase())
  )

  const getTypeBadge = (type: ShippingMethod['type']) => {
    switch (type) {
      case 'free':
        return (
<Badge tone="success">Free</Badge>
        )
      case 'flat':
        return <Badge tone="soft">Flat rate</Badge>
      case 'weight':
        return <Badge tone="soft">Weight based</Badge>
      case 'price':
        return <Badge tone="soft">Price based</Badge>
    }
  }

  return (
    <div>
      <div className="border-t border-border-subtle p-2">
        <div className="relative w-full sm:w-64">
          <Search className="absolute left-2.5 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            placeholder="Search methods"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-8"
          />
        </div>
      </div>

      <div className="border-t border-border-subtle">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Method</TableHead>
              <TableHead>Zone</TableHead>
              <TableHead>Type</TableHead>
              <TableHead className="text-right">Cost</TableHead>
              <TableHead>Delivery time</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="w-12"><span className="sr-only">Actions</span></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filteredMethods.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} className="py-8 text-center text-text-2">
                  <Truck className="mx-auto mb-2 size-8 text-muted-foreground" />
                  <p className="text-sm">No shipping methods found</p>
                </TableCell>
              </TableRow>
            ) : (
              filteredMethods.map((method) => (
                <TableRow key={method.id}>
                  <TableCell className="font-medium text-ink">
                    {method.name}
                    {method.minOrderAmount && (
                      <span className="block text-sm font-normal text-text-2">
                        Minimum order {formatCurrency(method.minOrderAmount)}
                      </span>
                    )}
                  </TableCell>
                  <TableCell>{method.zoneName}</TableCell>
                  <TableCell>{getTypeBadge(method.type)}</TableCell>
                  <TableCell className="text-right tabular-nums">
                    {method.type === 'free' ? 'Free' : formatCurrency(method.cost)}
                  </TableCell>
                  <TableCell className="text-sm text-text-2">
                    {method.estimatedDays || '-'}
                  </TableCell>
                  <TableCell>
                    <Badge tone={method.enabled ? 'success' : 'soft'}>
                      {method.enabled ? 'Active' : 'Inactive'}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="icon" aria-label="Actions">
                          <MoreVertical className="size-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem>
                          <Edit className="size-4" />
                          Edit
                        </DropdownMenuItem>
                        <DropdownMenuItem className="text-destructive">
                          <Trash2 className="size-4" />
                          Delete
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  )
}
